import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { Route } from 'nextjs-routes';
import { type FC } from 'react';

import { useProfile } from '@/hooks/useProfile';
import { useUserCurrentSubscription } from '@/hooks/useUserCurrentSubscription';
import { EnumAccountType } from '@/services/auth/auth';

import type { ButtonVariantProps } from './Button';
import Button from './Button';

type Props = {
  isUnlimitedSubscription?: boolean;
  className?: string;
} & ButtonVariantProps;

const SubscribeButton: FC<Props> = ({
  isUnlimitedSubscription,
  size,
  variant,
  className,
}) => {
  const { pathname, asPath } = useRouter();
  const { data: profileData } = useProfile();
  const { data: userCurrentSubscriptionData } = useUserCurrentSubscription();

  if (!profileData) return null;

  const { accountType } = profileData;

  const isAdminOrCustomer =
    accountType === EnumAccountType.CUSTOMER ||
    accountType === EnumAccountType.ADMIN;

  const hasActiveSubscription =
    isUnlimitedSubscription || userCurrentSubscriptionData;

  const buttonInfo = (() => {
    const isSubscriptionActive =
      hasActiveSubscription || userCurrentSubscriptionData;

    const basePath = isAdminOrCustomer
      ? '/pricing/recruiter'
      : '/pricing/candidate';

    const label = isSubscriptionActive ? 'Manage' : 'Subscribe';

    const isOnSubscribePage =
      pathname.includes(basePath) && label === 'Subscribe';

    const href: Route = isSubscriptionActive
      ? {
          pathname: `/pricing/customer-portal`,
          query: { callbackUrl: asPath },
        }
      : { pathname: basePath };

    if (!isOnSubscribePage) return { label, href };
    return null;
  })();

  if (!buttonInfo) return null;

  return (
    <Link href={buttonInfo?.href} className={classNames('flex', className)}>
      <Button tabIndex={-1} size={size} variant={variant} className="w-full">
        {buttonInfo?.label}
      </Button>
    </Link>
  );
};

export default SubscribeButton;
